import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import {
  service_request,
  vendor_professional_list,
  vendor_professional_enquriylist,
  delete_vendor_service, // Import the new API function
} from "../../../api/ControllApi";
import { get_vendor_store } from "../../../api/Role";
import { useUser } from "../../../context/LoginDetail";
import { useNavigate } from "react-router-dom";

export default function AddServiceForm() {
  const [materials, setMaterials] = useState([]);
  const [materialList, setMaterialList] = useState([]);
  const [selectedserviceId, setSelectedserviceId] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();
  const [hasCompany, setHasCompany] = useState(false);
  const [checkLoading, setCheckLoading] = useState(true);

  // Deletion Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const PLACEHOLDER_IMG = "https://via.placeholder.com/50x50?text=No+Image";

  const getServiceList = async () => {
    try {
      const res = await vendor_professional_list();
      setMaterialList(res?.data?.services || []);
    } catch (err) {

    }
  };

  const getEnquiryList = async () => {
    setTableLoading(true);
    try {
      const res = await vendor_professional_enquriylist();
      setMaterials(res?.data || []);
    } catch (err) {

      setMaterials([]);
    } finally {
      setTableLoading(false);
    }
  };

  const checkCompanyProfile = async () => {
    if (!user?.id) return;
    setCheckLoading(true);
    try {
      const res = await get_vendor_store(user.id);
      if (res?.data) {
        setHasCompany(true);
      } else {
        setHasCompany(false);
      }
    } catch (err) {

      setHasCompany(false);
    } finally {
      setCheckLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkCompanyProfile();
    }
  }, [user?.id]);

  useEffect(() => {
    getServiceList();
    getEnquiryList();
  }, []);

  const handleQuery = async () => {
    if (!selectedserviceId) {
      toast.error("Please select service");
      return;
    }
    setLoading(true);
    try {
      const res = await service_request({
        service_id: Number(selectedserviceId),
      });
      if (res?.status === 200) {
        toast.success("Service added successfully");
        setSelectedserviceId("");
        setOpen(false);
        getEnquiryList();
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error("Service already exists");
      } else {
        toast.error(error?.response?.data?.message || "API Error");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- UPDATED DELETE HANDLER ---
  const handleDelete = (id) => {
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    setDeleteLoading(true);
    try {
      const res = await delete_vendor_service(itemToDelete);
      if (res.status === 200) {
        toast.success("Service deleted successfully");
        getEnquiryList();
      } else {
        toast.error("Failed to delete the service");
      }
    } catch (error) {

      toast.error(error?.response?.data?.message || "Error deleting service");
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  if (checkLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress sx={{ color: "#7E9F40" }} />
      </Box>
    );
  }

  if (!hasCompany) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, textAlign: "center", mt: 5 }}>
        <Typography variant="h6" gutterBottom>
          Please complete your Company Profile to add services.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/dashboard/company-profile")}
          sx={{ bgcolor: "#7E9F40", mt: 2, textTransform: "none" }}
        >
          Go to Company Profile
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight="bold">Add Service List</Typography>
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          sx={{ bgcolor: "#7E9F40", textTransform: "none", "&:hover": { bgcolor: "#6a8a35" } }}
        >
          Add Service
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead sx={{ bgcolor: "#F5F5F5" }}>
            <TableRow>
              <TableCell><b>Service Name</b></TableCell>
              <TableCell><b>Image</b></TableCell>
              <TableCell align="right"><b>Action</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {tableLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} sx={{ color: "#7E9F40" }} />
                </TableCell>
              </TableRow>
            ) : materials.length > 0 ? (
              materials.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item?.service?.service_name || "-"}</TableCell>
                  <TableCell>
                    <img
                      src={item?.service?.image?.[0] || PLACEHOLDER_IMG}
                      alt="service"
                      width={50}
                      height={50}
                      style={{ borderRadius: 6, objectFit: "cover" }}
                      onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => handleDelete(item.id)} 
                      sx={{ color: "#d32f2f" }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  No Service Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* POPUP */}
      <Dialog open={open} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>
          Add Service List
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Select
              fullWidth
              value={selectedserviceId}
              displayEmpty
              onChange={(e) => setSelectedserviceId(e.target.value)}
              size="small"
            >
              <MenuItem value=""><em>Select Service</em></MenuItem>
              {materialList.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.service_name}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant="contained"
              onClick={handleQuery}
              disabled={loading}
              fullWidth
              sx={{ bgcolor: "#7E9F40", textTransform: "none", py: 1, "&:hover": { bgcolor: "#6a8a35" } }}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* DELETION CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this service? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleteLoading}
            sx={{ textTransform: "none", color: "grey.600" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            disabled={deleteLoading}
            variant="contained"
            color="error"
            autoFocus
            sx={{ textTransform: "none" }}
          >
            {deleteLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}